// src/context/UserContext.tsx
import { notify } from "@/components/common/Notification";
import {
  clearLocalStorage,
  getAccessToken,
  getRefreshToken,
  getTenantId,
  getUserInfo,
  setLocalAccessToken,
  setLocalRefreshToken,
  setLocalTenantId,
  setLocalUserInfo,
} from "@/lib/storeManager";
import { AuthService, UserPreferencesService } from "@/services/APIs";
import { AuthRes, SocialLoginRes } from "@/utils/types/auth";
import {
  ACCESS_TOKEN_REFRESH_INTERVAL_MS,
  refreshAccessToken,
} from "@/lib/tokenRefresh";
import { getApiErrorMessage } from "@project/lib/api-error";
import { jwtDecode } from "jwt-decode";
import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { setCredentials, clearCredentials } from "@/store/slices/authSlice";

interface DecodedToken {
  userId: string;
  email: string;
  exp: number;
  [key: string]: any;
}

interface UserContextType {
  jwtToken: string | null;
  refreshToken: string | null;
  decodedToken: DecodedToken | null;
  isAuthenticated: boolean;
  preferencesData: any; // Added preferences data
  fetchPreferencesData: () => Promise<void>; // Function to fetch preferences data
  signup: (
    email: string,
    password: string,
    first_name: string,
    last_name: string
  ) => Promise<any>;
  login: (email: string, password: string) => Promise<AuthRes | undefined>;
  logout: () => Promise<any>;
  socialLogin: (provider: string) => Promise<SocialLoginRes | undefined>;
  forgotPassword: (email: string) => Promise<any>;
  forgotPasswordReset: (
    id: string,
    payload: {
      new_password: string;
      confirm_password: string;
    }
  ) => Promise<any>;
  resetPassword: (values: {
    current_password: string;
    new_password: string;
  }) => Promise<any>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  // Redux and local state
  const dispatch = useAppDispatch();
  const jwtToken = useAppSelector(state => state.auth.accessToken);
  const refreshToken = useAppSelector(state => state.auth.refreshToken);
  const [decodedToken, setDecodedToken] = useState<DecodedToken | null>(null);
  const [preferencesData, setPreferencesData] = useState<any>(null); // Preferences data state

  // Derive decoded token from jwtToken
  useEffect(() => {
    if (jwtToken) {
      try {
        const decoded = jwtDecode<DecodedToken>(jwtToken);
        setDecodedToken(decoded);
      } catch {
        setDecodedToken(null);
      }
    } else {
      setDecodedToken(null);
    }
  }, [jwtToken]);

  // Bootstrap from localStorage
  useEffect(() => {
    const access = getAccessToken();
    const refresh = getRefreshToken();
    const storedUser = getUserInfo();
    if (access && refresh && !jwtToken) {
      try {
        const decoded = jwtDecode<DecodedToken>(access);
        dispatch(
          setCredentials({
            user: storedUser ?? { email: decoded.email },
            accessToken: access,
            refreshToken: refresh,
          })
        );
        setDecodedToken(decoded);
      } catch {
        const preservedTenant = getTenantId();
        clearLocalStorage();
        if (preservedTenant !== null) {
          setLocalTenantId(preservedTenant);
        }
      }
    }
  }, [dispatch, jwtToken]);

  const isAuthenticated =
    Boolean(jwtToken && decodedToken) && decodedToken!.exp * 1000 > Date.now();

  // Fetch preferences data function
  const fetchPreferencesData = useCallback(async () => {
    const data = await UserPreferencesService.getUserPreferencesGrid();
    if (data) {
      setPreferencesData(data);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchPreferencesData();
    }
  }, [isAuthenticated, decodedToken, refreshToken, fetchPreferencesData]);

  const handleLogout = useCallback(async () => {
    try {
      await AuthService.logout();
    } catch {
      // Clear local session even if the API call fails
    } finally {
      dispatch(clearCredentials());
      setDecodedToken(null);
      setPreferencesData(null);
      const preservedTenant =
        getTenantId() ?? import.meta.env.VITE_X_TENANTID ?? null;
      clearLocalStorage();
      if (preservedTenant) {
        setLocalTenantId(preservedTenant);
      }
    }
  }, [dispatch]);

  const scheduleRefresh = useCallback(() => {
    if (!refreshToken || !jwtToken) {
      return;
    }

    const runRefresh = async () => {
      try {
        const tokens = await refreshAccessToken();
        const access = tokens.access;
        const refresh = tokens.refresh ?? refreshToken;
        const decoded = jwtDecode<DecodedToken>(access);
        const storedUser = getUserInfo() ?? { email: decoded.email };
        dispatch(
          setCredentials({
            user: storedUser,
            accessToken: access,
            refreshToken: refresh,
          })
        );
        setDecodedToken(decoded);
      } catch {
        await handleLogout();
      }
    };

    const checkRefresh = () => {
      try {
        const decoded = jwtDecode<DecodedToken>(jwtToken);
        const expMs = decoded.exp * 1000;
        const iatMs = (decoded.iat ?? (decoded.exp - 15 * 60)) * 1000;
        const nowMs = Date.now();
        const totalDuration = expMs - iatMs;
        const buffer = Math.max(5 * 60 * 1000, totalDuration * 0.1);

        if (expMs - nowMs <= buffer) {
          void runRefresh();
        }
      } catch (err) {
        console.error("Failed to check user token expiry", err);
      }
    };

    // Run check immediately
    checkRefresh();

    const intervalId = window.setInterval(() => {
      checkRefresh();
    }, ACCESS_TOKEN_REFRESH_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [refreshToken, jwtToken, handleLogout, dispatch]);

  useEffect(() => {
    if (!isAuthenticated || !refreshToken) {
      return;
    }
    const cleanup = scheduleRefresh();
    return cleanup;
  }, [isAuthenticated, refreshToken, scheduleRefresh]);

  const handleSignup = async (
    userEmail: string,
    userPassword: string,
    userFirstName: string,
    userLastName: string
  ) => {
    const response = await AuthService.signup({
      email: userEmail,
      password: userPassword,
      first_name: userFirstName,
      last_name: userLastName,
    });
    type TokenPayload = { access: string; refresh: string };
    const payload = (response?.data ?? response) as TokenPayload;
    const access = payload?.access;
    const refresh = payload?.refresh;

    if (response) {
      const {
        first_name,
        last_name,
        email: responseEmail,
        user_type,
      } = response.user;
      const userInfo = {
        first_name,
        last_name,
        email: responseEmail,
        user_type,
      };
      dispatch(
        setCredentials({
          user: userInfo,
          accessToken: access,
          refreshToken: refresh,
        })
      );
      setLocalAccessToken(access);
      setLocalRefreshToken(refresh);
      setLocalUserInfo(userInfo);
      fetchPreferencesData();
      return response;
    }
  };

  const handleLogin = async (userEmail: string, userPassword: string) => {
    try {
      const response = await AuthService.login({
        email: userEmail,
        password: userPassword,
      });
      if (!response?.access) {
        throw new Error("Invalid email or password");
      }

      const {
        first_name,
        last_name,
        email: responseEmail,
        user_type,
      } = response.user;
      const userInfo = {
        first_name,
        last_name,
        email: responseEmail,
        user_type,
      };
      dispatch(
        setCredentials({
          user: userInfo,
          accessToken: response.access,
          refreshToken: response.refresh,
        })
      );
      setLocalAccessToken(response.access);
      setLocalRefreshToken(response.refresh);
      setLocalUserInfo(userInfo);
      fetchPreferencesData();
      return response;
    } catch (error) {
      throw new Error(getApiErrorMessage(error, "Invalid email or password"));
    }
  };

  const handleForgotPassword = async (userEmail: string) => {
    const response = await AuthService.forgotPassword(userEmail);
    if (response) {
      notify.success("Reset password email sent successfully");
      return response;
    }
  };

  const handleForgotPasswordReset = async (
    id: string,
    payload: {
      new_password: string;
      confirm_password: string;
    }
  ) => {
    const response = await AuthService.forgotPasswordReset(id, payload);
    if (response) {
      notify.success("Password reset successfully");
      return response;
    }
  };

  const handleResetPassword = async (values: {
    current_password: string;
    new_password: string;
  }) => {
    const response = await AuthService.resetPassword(values);
    return response;
  };

  const handleSocialLogin = async (provider: string) => {
    const response = await AuthService.socialLogin(provider);
    return response;
  };

  return (
    <UserContext.Provider
      value={{
        jwtToken,
        refreshToken,
        decodedToken,
        isAuthenticated,
        preferencesData,
        fetchPreferencesData,
        signup: handleSignup,
        login: handleLogin,
        logout: handleLogout,
        socialLogin: handleSocialLogin,
        forgotPassword: handleForgotPassword,
        forgotPasswordReset: handleForgotPasswordReset,
        resetPassword: handleResetPassword,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

// Export the context for use in other files
export { UserContext };
